import { useState } from "react";
import { FileUp, ShieldCheck, UserCheck } from "lucide-react";

import { api, errMsg } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { uploadDocumentOnly } from "../../../lib/resumeUpload.js";
import { AsyncButton } from "../../ui/async-button.js";
import { Badge, type BadgeProps } from "../../ui/badge.js";
import { Button } from "../../ui/button.js";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog.js";
import { FileInput } from "../../ui/file-input.js";
import { Label } from "../../ui/label.js";
import { SelectField } from "../../ui/select-field.js";
import { Textarea } from "../../ui/textarea.js";
import { DocVersionHistory } from "../../DocVersionHistory.js";
import type { LightboxTarget } from "../../DocumentLightbox.js";
import { ReferenceStatusBadge } from "../../onboarding/ReferenceStatusBadge.js";

type PersonName = { firstName: string; lastName: string } | null;

export interface ReviewableReference {
  id: string; name: string; relationship: string; company: string; designation: string | null;
  /** PROFESSIONAL | ACADEMIC — academic means the company field holds a university. */
  referenceType?: string;
  phone: string; email: string | null; status: string; hrComments: string | null;
  reviewedAt: string | null;
  reviewedBy: { employee: PersonName } | null;
}

export interface BgvState {
  bgvStatus: string | null; bgvNotes: string | null; bgvUpdatedAt: string | null; bgvUpdatedBy: PersonName;
}

type ReferenceAction = "VERIFY" | "FLAG_UNREACHABLE" | "REQUEST_ALTERNATE";
interface ReturnResult { returned: boolean; emailed: boolean; emailError: string | null }

const BGV_STATUS: Record<string, { label: string; tone: BadgeProps["tone"] }> = {
  PENDING:     { label: "Pending", tone: "neutral" },
  IN_PROGRESS: { label: "In progress", tone: "info" },
  VERIFIED:    { label: "Verified", tone: "success" },
  FAILED:      { label: "Failed", tone: "error" },
};

const ACTIONS: Record<ReferenceAction, { title: string; button: string; variant: "success" | "warning" | "destructive"; needsComment: boolean; help: string }> = {
  VERIFY:            { title: "Verify reference", button: "Mark verified", variant: "success", needsComment: false, help: "You spoke to this reference and they confirmed the employee." },
  FLAG_UNREACHABLE:  { title: "Flag as uncontactable", button: "Flag unreachable", variant: "warning", needsComment: true, help: "The comment is shown to the employee. The reference stops counting towards the 2 needed." },
  REQUEST_ALTERNATE: { title: "Request an alternate reference", button: "Request alternate", variant: "destructive", needsComment: true, help: "Sends the onboarding back to the employee with your comment and a fresh link, asking them to add another reference." },
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const fullName = (p: PersonName) => (p ? `${p.firstName} ${p.lastName}` : null);

// HR side of background verification: import the BGV report, record the
// status with a note (VERIFIED needs the report), and review each
// professional reference the employee gave.
export function BgvReferencesPanel({ employeeId, bgv, references, hasBgvReport, canEdit, disabledReason, docRefreshKey, bumpDocRefresh, setVerifyTarget, onChanged }: {
  employeeId: string;
  bgv: BgvState;
  references: ReviewableReference[];
  hasBgvReport: boolean;
  canEdit: boolean;
  disabledReason?: string | undefined;
  docRefreshKey: number;
  bumpDocRefresh: (type: string) => void;
  setVerifyTarget: (t: LightboxTarget) => void;
  onChanged: () => Promise<unknown>;
}) {
  const [reportFile, setReportFile] = useState<File | null>(null);
  const [bgvOpen, setBgvOpen] = useState(false);
  const [bgvStatus, setBgvStatus] = useState(bgv.bgvStatus ?? "IN_PROGRESS");
  const [bgvNotes, setBgvNotes] = useState(bgv.bgvNotes ?? "");
  const [refTarget, setRefTarget] = useState<{ ref: ReviewableReference; action: ReferenceAction } | null>(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [returnNote, setReturnNote] = useState<ReturnResult | null>(null);

  async function importReport() {
    if (!reportFile) return;
    setError("");
    try {
      await uploadDocumentOnly(employeeId, reportFile, "BGV_REPORT", { name: "Background Verification Report" });
      setReportFile(null);
      bumpDocRefresh("BGV_REPORT");
    } catch (e) {
      logError("BgvReferencesPanel.importReport", e, { employeeId });
      setError(errMsg(e) ?? "Could not import the report.");
      throw e;
    }
  }

  async function saveBgv() {
    setError("");
    try {
      await api.patch(`/employees/${employeeId}/onboarding/bgv`, { bgvStatus, notes: bgvNotes.trim() || undefined });
      await onChanged();
      setBgvOpen(false);
    } catch (e) {
      logError("BgvReferencesPanel.saveBgv", e, { employeeId });
      setError(errMsg(e) ?? "Could not update background verification.");
      throw e;
    }
  }

  async function reviewRef() {
    if (!refTarget) return;
    setError("");
    if (ACTIONS[refTarget.action].needsComment && !comment.trim()) {
      setError("Add a comment for the employee explaining why.");
      throw new Error("validation");
    }
    try {
      const { data } = await api.patch<{ returned: ReturnResult | null }>(`/references/${refTarget.ref.id}`, {
        action: refTarget.action, comment: comment.trim() || undefined,
      });
      setReturnNote(data.returned);
      await onChanged();
      setRefTarget(null);
    } catch (e) {
      logError("BgvReferencesPanel.reviewRef", e, { referenceId: refTarget.ref.id });
      setError(errMsg(e) ?? "Could not save the review.");
      throw e;
    }
  }

  const status = bgv.bgvStatus ? BGV_STATUS[bgv.bgvStatus] : null;
  const verifiedRefs = references.filter(r => r.status === "VERIFIED").length;

  return (
    <>
      {/* ── Background verification ── */}
      <div className="border-b border-surface-100 py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-[1_1_220px] flex-wrap items-center gap-2">
            <span className="text-[13px] font-medium">Background verification</span>
            {status ? <Badge tone={status.tone}>{status.label}</Badge> : <Badge tone="outline">Not started</Badge>}
            {bgv.bgvUpdatedAt && (
              <span className="text-xs text-surface-500">
                updated {fmtDate(bgv.bgvUpdatedAt)}{fullName(bgv.bgvUpdatedBy) && <> by {fullName(bgv.bgvUpdatedBy)}</>}
              </span>
            )}
          </div>
          <Button size="sm" variant="secondary" disabled={!canEdit} title={canEdit ? undefined : disabledReason}
            onClick={() => { setBgvStatus(bgv.bgvStatus ?? "IN_PROGRESS"); setBgvNotes(bgv.bgvNotes ?? ""); setError(""); setBgvOpen(true); }}>
            <ShieldCheck size={12} /> Update status
          </Button>
        </div>
        {bgv.bgvNotes && <p className="mt-1 text-xs text-surface-600">“{bgv.bgvNotes}”</p>}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-surface-600">BGV report</span>
          {!hasBgvReport && <span className="text-xs text-surface-500">Not imported yet — required before marking BGV verified.</span>}
          {canEdit && (
            <>
              <FileInput size="sm" value={reportFile} onChange={setReportFile} accept=".pdf,image/*" placeholder={hasBgvReport ? "Choose a newer report" : "Choose report file"} />
              <AsyncButton size="sm" variant="outline" animated={false} disabled={!reportFile} onClick={() => importReport()}>
                <FileUp size={12} /> Import report
              </AsyncButton>
            </>
          )}
        </div>
        {hasBgvReport && (
          <DocVersionHistory employeeId={employeeId} docType="BGV_REPORT" isHR refreshKey={docRefreshKey} onVerify={setVerifyTarget} />
        )}
      </div>

      {/* ── References ── */}
      <div className="border-b border-surface-100 py-2.5">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <span className="text-[13px] font-medium">References</span>
          <span className="text-xs text-surface-500">{verifiedRefs} of {references.length} verified</span>
        </div>
        {returnNote && (
          <p className="mb-2 rounded-md border border-[var(--color-warning-border)] bg-[var(--color-warning-bg-subtle)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
            {returnNote.returned ? "Onboarding sent back to the employee. " : ""}
            {returnNote.emailed ? "They were emailed a fresh link." : `Email not sent — ${returnNote.emailError ?? "unknown reason"}. Send them a new link from "Onboarding link" above.`}
          </p>
        )}
        {references.length === 0 && <p className="text-xs text-surface-500">The employee hasn't added references yet.</p>}
        <ul className="divide-y divide-surface-100">
          {references.map(r => (
            <li key={r.id} className="flex flex-wrap items-start gap-x-3 gap-y-1.5 py-2">
              <div className="min-w-0 flex-[1_1_240px]">
                <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium">
                  <span>{r.name} <span className="font-normal text-surface-500">· {r.relationship}</span></span>
                  {/* Tells HR to expect a university switchboard, not an HR desk. */}
                  {r.referenceType === "ACADEMIC" && <Badge tone="outline">Academic</Badge>}
                </div>
                <div className="text-xs text-surface-500">
                  {[r.designation, r.company].filter(Boolean).join(", ")} · {r.phone}{r.email && <> · {r.email}</>}
                </div>
                {r.hrComments && <div className="text-xs text-surface-600">“{r.hrComments}”</div>}
                {r.reviewedAt && (
                  <div className="text-[11px] text-surface-400">
                    reviewed {fmtDate(r.reviewedAt)}{fullName(r.reviewedBy?.employee ?? null) && <> by {fullName(r.reviewedBy?.employee ?? null)}</>}
                  </div>
                )}
              </div>
              <ReferenceStatusBadge status={r.status} />
              {r.status !== "ALTERNATE_REQUESTED" && (
                <div className="flex flex-wrap gap-1.5">
                  {(Object.keys(ACTIONS) as ReferenceAction[])
                    .filter(a => !(a === "VERIFY" && r.status === "VERIFIED") && !(a === "FLAG_UNREACHABLE" && r.status === "UNREACHABLE"))
                    .map(a => (
                      <Button key={a} size="sm" variant="secondary" disabled={!canEdit} title={canEdit ? undefined : disabledReason}
                        onClick={() => { setRefTarget({ ref: r, action: a }); setComment(r.hrComments ?? ""); setError(""); }}>
                        {a === "VERIFY" && <UserCheck size={12} />} {ACTIONS[a].button}
                      </Button>
                    ))}
                </div>
              )}
            </li>
          ))}
        </ul>
        {error && !bgvOpen && !refTarget && <p className="mt-1 text-xs text-[var(--color-error-fg)]">{error}</p>}
      </div>

      <Dialog open={bgvOpen} onOpenChange={(o) => { if (!o) setBgvOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Background verification status</DialogTitle>
            <DialogDescription>Recorded with your name and today's date.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label htmlFor="bgv-status">Status</Label>
              <SelectField id="bgv-status" value={bgvStatus} onChange={e => setBgvStatus(e.target.value)}>
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In progress</option>
                <option value="VERIFIED" disabled={!hasBgvReport}>Verified{hasBgvReport ? "" : " — import the report first"}</option>
                <option value="FAILED">Failed</option>
              </SelectField>
            </div>
            <div>
              <Label htmlFor="bgv-notes">Notes</Label>
              <Textarea id="bgv-notes" rows={3} value={bgvNotes} onChange={e => setBgvNotes(e.target.value)} placeholder="e.g. Agency, findings, discrepancies" />
            </div>
            {error && <p className="text-xs text-[var(--color-error-fg)]">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setBgvOpen(false)}>Cancel</Button>
            <AsyncButton onClick={() => saveBgv()} variant={bgvStatus === "VERIFIED" ? "success" : bgvStatus === "FAILED" ? "destructive" : "default"}>
              Save
            </AsyncButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!refTarget} onOpenChange={(o) => { if (!o) setRefTarget(null); }}>
        <DialogContent className="max-w-md">
          {refTarget && (
            <>
              <DialogHeader>
                <DialogTitle>{ACTIONS[refTarget.action].title}</DialogTitle>
                <DialogDescription>{refTarget.ref.name} · {refTarget.ref.company} · {refTarget.ref.phone}</DialogDescription>
              </DialogHeader>
              <p className="text-xs text-surface-600">{ACTIONS[refTarget.action].help}</p>
              <div>
                <Label htmlFor="ref-comment">Comment{ACTIONS[refTarget.action].needsComment ? " (required)" : " (optional)"}</Label>
                <Textarea id="ref-comment" rows={3} value={comment} onChange={e => setComment(e.target.value)}
                  placeholder={refTarget.action === "VERIFY" ? "e.g. Confirmed role and dates" : "e.g. Number not reachable after 3 attempts"} />
              </div>
              {error && <p className="text-xs text-[var(--color-error-fg)]">{error}</p>}
              <DialogFooter>
                <Button variant="secondary" onClick={() => setRefTarget(null)}>Cancel</Button>
                <AsyncButton onClick={() => reviewRef()} variant={ACTIONS[refTarget.action].variant}>
                  {ACTIONS[refTarget.action].button}
                </AsyncButton>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
